// Line Execute — Confirm resolvers.
//
// Real mutation code for actions that returned NEEDS_CONFIRMATION. Each
// resolver is small, catches its own expected errors, and never throws.

import { cancelMeeting } from '../../meetingService'
import { createEmailInvite, createPhoneInvite } from '../../inviteService'
import { runMeetingConcierge } from '../../meetingConciergeService'
import type { MeetingBusySlot, MeetingMember } from '../../meetingService'
import type { MeetingRecord } from '../../../types/meetings'
import { registerLineExecuteConfirmResolver } from './confirmPayloadHandler'
import type { LineExecuteContext, LineExecuteOutcome } from './dispatcher'

interface MeetingCancelPayload {
  kind: 'MEETING_CANCEL'
  meetingIds: string[]
}

interface MemberInvitePayload {
  kind: 'MEMBER_INVITE'
  email: string | null
  phone: string | null
  role: string
}

// MEETING_SCHEDULE: re-runs the concierge (which now includes the premium
// action reservation) and hands the resulting draft back. Full creation via
// createMeeting is still gated by the wizard so the user can pick a provider
// / participants; we surface a link chip to open Meetings pre-filled.
registerLineExecuteConfirmResolver('MEETING_SCHEDULE', async (
  _payload,
  context: LineExecuteContext,
): Promise<LineExecuteOutcome> => {
  try {
    const draft = await runMeetingConcierge(context.workspaceId, context.prompt, {
      timezone: context.timezone,
      meetings: [] as MeetingRecord[],
      calendarBusySlots: [] as MeetingBusySlot[],
      members: [] as MeetingMember[],
    })
    return {
      kind: 'COMPLETED',
      message: `Meeting concierge reserved and drafted "${draft.title}" — I opened Meetings so you can approve the final invite.`,
      navigateTo: `/console/workspace/${context.workspaceId}/meetings`,
      toast: { level: 'success', message: 'Meeting draft ready' },
    }
  } catch (error) {
    return {
      kind: 'FAILED',
      message: error instanceof Error ? error.message : 'I could not run the meeting concierge.',
      recoverable: true,
    }
  }
})

registerLineExecuteConfirmResolver('MEETING_CANCEL', async (
  payload,
): Promise<LineExecuteOutcome> => {
  const meetingIds = (payload as unknown as MeetingCancelPayload).meetingIds ?? []
  const failed: string[] = []
  for (const meetingId of meetingIds) {
    try {
      await cancelMeeting(meetingId)
    } catch {
      failed.push(meetingId)
    }
  }
  if (failed.length > 0) {
    return {
      kind: 'FAILED',
      message: `Could not cancel ${failed.length} meeting${failed.length === 1 ? '' : 's'}.`,
      recoverable: true,
    }
  }
  return {
    kind: 'COMPLETED',
    message: `Cancelled ${meetingIds.length} meeting${meetingIds.length === 1 ? '' : 's'}.`,
    toast: { level: 'success', message: 'Meeting cancelled' },
  }
})

registerLineExecuteConfirmResolver('MEMBER_INVITE', async (
  payload,
  context: LineExecuteContext,
): Promise<LineExecuteOutcome> => {
  const { email, phone, role } = payload as unknown as MemberInvitePayload
  const invitedByName = context.actor.displayName ?? 'A workspace admin'
  try {
    const base = {
      workspaceId: context.workspaceId,
      workspaceName: context.workspaceId,
      role: role.toUpperCase() as Parameters<typeof createEmailInvite>[0]['role'],
      invitedBy: context.actor.uid,
      invitedByName,
    }
    if (email) {
      await createEmailInvite({ ...base, email })
    } else if (phone) {
      await createPhoneInvite({ ...base, phoneE164: phone })
    } else {
      return { kind: 'FAILED', message: 'No contact provided.', recoverable: true }
    }
    return {
      kind: 'COMPLETED',
      message: `Invite sent to ${email ?? phone}.`,
      toast: { level: 'success', message: 'Invite sent' },
    }
  } catch (error) {
    return {
      kind: 'FAILED',
      message: error instanceof Error ? error.message : 'Failed to send the invite.',
      recoverable: true,
    }
  }
})

// TIMELINE_STATUS_CHANGE and CHECKLIST_TOGGLE_DONE resolvers are stubbed —
// they require callbacks the panel owns (updateElement, checklistWriter),
// which we thread via context.canPerform-adjacent hooks in Phase 4. For now
// they return a FAILED envelope so the confirm chip has an audible response.
registerLineExecuteConfirmResolver('TIMELINE_STATUS_CHANGE', async () => ({
  kind: 'FAILED',
  message: 'Status changes need a permission-gated hook that Phase 4 wires. Coming next.',
  recoverable: false,
}))

registerLineExecuteConfirmResolver('CHECKLIST_TOGGLE_DONE', async () => ({
  kind: 'FAILED',
  message: 'Checklist toggle needs a permission-gated hook that Phase 4 wires. Coming next.',
  recoverable: false,
}))

registerLineExecuteConfirmResolver('CHAT_SEND', async () => ({
  kind: 'FAILED',
  message: 'Chat posting needs a group-channel routing hook. Coming next.',
  recoverable: false,
}))

let booted = false
export const ensureLineExecuteConfirmResolversRegistered = (): void => {
  if (booted) return
  booted = true
  // Registrations are side-effect imports above; this function exists so
  // the panel can force-load the module without relying on tree-shaker
  // behavior in dev builds.
}

export const __resetLineExecuteConfirmResolverBootForTests = (): void => {
  booted = false
}
